import axios, { AxiosInstance, AxiosResponse } from "axios";
import { ApiResponse } from "../types";
import { formatAxiosErrorMessage } from "../utils/apiErrorMessage";

/**
 * Module-level storage for trace context.
 * This allows the ApiClient to include traceparent header in requests
 * when an AI Job is active.
 */
let currentTraceparent: string | null = null;

/**
 * Set the current traceparent for API requests.
 * Called by AIJobContext when a job is initialized.
 */
const isDev =
  typeof process !== "undefined" && process.env.NODE_ENV === "development";

export const setTraceparent = (traceparent: string | null): void => {
  currentTraceparent = traceparent;
  if (isDev) {
    console.log(
      "[ApiClient] Traceparent set:",
      traceparent ? traceparent.substring(0, 20) + "..." : null
    );
  }
};

/**
 * Get the current traceparent.
 */
export const getTraceparent = (): string | null => {
  return currentTraceparent;
};

class ApiClient {
  private client: AxiosInstance;

  constructor(
    baseURL: string = `${
      process.env.REACT_APP_API_URL || window.location.origin
    }/v1`
  ) {
    if (isDev) {
      console.log("ApiClient initialized with:");
      console.log("  REACT_APP_API_URL:", process.env.REACT_APP_API_URL);
      console.log("  baseURL:", baseURL);
    }
    this.client = axios.create({
      baseURL,
      timeout: 60000,
      headers: {
        "Content-Type": "application/json",
      },
      withCredentials: true,
    });

    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Add any auth tokens or other headers here
        const token = this.getAuthToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Add traceparent header for ai_job_monitoring if available
        if (currentTraceparent) {
          config.headers.traceparent = currentTraceparent;
        }

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        if (error.response?.status === 401) {
          // Handle unauthorized - redirect to auth login
          // Pass current URL as next_url so that project_id and other query params are preserved after auth
          const API_BASE_URL =
            process.env.REACT_APP_API_URL || window.location.origin;
          const nextUrl = encodeURIComponent(window.location.href);
          window.location.href = `${API_BASE_URL}/v1/auth/login?prompt=login&next_url=${nextUrl}`;
        }
        return Promise.reject(error);
      }
    );
  }

  private getAuthToken(): string | null {
    // For Auth0 cookie-based authentication, we don't need to manually get the token
    // The cookies will be automatically sent with requests due to withCredentials: true
    return null;
  }

  public async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.get(url, { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: formatAxiosErrorMessage(error),
        status: (error as { response?: { status?: number } })?.response?.status,
      };
    }
  }

  public async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: formatAxiosErrorMessage(error),
      };
    }
  }

  public async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.put(url, data);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: formatAxiosErrorMessage(error),
      };
    }
  }

  public async delete<T>(url: string): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.delete(url);
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: formatAxiosErrorMessage(error),
      };
    }
  }

  public async postForm<T>(
    url: string,
    formData: FormData
  ): Promise<ApiResponse<T>> {
    try {
      const response = await this.client.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: formatAxiosErrorMessage(error),
      };
    }
  }

  public async stream(url: string, params?: any): Promise<ReadableStream> {
    const response = await this.client.get(url, {
      params,
      responseType: "stream",
    });
    return response.data;
  }
}

export const apiClient = new ApiClient();
